import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { visibleStateDelayMs } from "./tableConfig";

export default function useBlackjackTable(tableId) {
  const [state, setState] = useState(null);
  const [transport, setTransport] = useState("polling");
  // First-load error: set only when the first state fetch fails while state is
  // still null. Lets the player table tell "table unavailable" apart from the
  // normal "waiting for the dealer to open betting". Cleared on any success.
  const [loadError, setLoadError] = useState("");
  const [receivedAt, setReceivedAt] = useState(Date.now());
  // Liveness signal for stale detection: refreshed on every successful contact
  // (poll or WS push), independent of whether the table revision advanced. An
  // idle table (unchanged revision) is still a healthy connection, so freshness
  // must not depend on revision changes or the player is falsely paused.
  const [lastContactAt, setLastContactAt] = useState(Date.now());
  const [, setClockTick] = useState(0);
  const aliveRef = useRef(true);
  const timersRef = useRef(new Set());
  const revisionRef = useRef(-1);

  const apply = useCallback((next) => {
    if (!next) return;
    const commit = () => {
      const revision = Number(next.revision ?? 0);
      if (revision <= revisionRef.current) return;
      revisionRef.current = revision;
      const visible = next.seconds_left == null
        ? next
        : { ...next, seconds_left: Math.max(0, Number(next.seconds_left) - (delay / 1000)) };
      setState(visible);
      setReceivedAt(Date.now());
    };
    const delay = visibleStateDelayMs(next);
    if (!delay) {
      commit();
      return;
    }
    const timer = setTimeout(() => {
      timersRef.current.delete(timer);
      commit();
    }, delay);
    timersRef.current.add(timer);
  }, []);

  const poll = useCallback(async () => {
    if (!tableId) return;
    const { data } = await api.get(`/casino/bj/state?table=${tableId}`);
    if (aliveRef.current) {
      setLastContactAt(Date.now());
      setLoadError(""); // a successful fetch clears any first-load error
      apply(data);
    }
  }, [apply, tableId]);

  useEffect(() => {
    aliveRef.current = true;
    revisionRef.current = -1;
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current.clear();
    setState(null);
    setTransport("polling");
    setLoadError("");
    if (!tableId) return () => { aliveRef.current = false; };

    let ws;
    let retry;
    let heartbeat;
    let closedByEffect = false;

    // First state load — a failure while state is still null surfaces a first-load
    // error ("table unavailable"); the 5s fallback poll keeps retrying and clears it.
    poll().catch((e) => {
      if (aliveRef.current) {
        setLoadError(e?.response?.data?.detail || "This table is temporarily unavailable. Retrying…");
      }
    });

    const connect = async () => {
      try {
        const { data } = await api.post(`/casino/bj/socket-ticket?table=${tableId}`);
        const wsBase = API.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
        ws = new WebSocket(`${wsBase}/ws/blackjack/${tableId}?ticket=${encodeURIComponent(data.ticket)}`);

        ws.onopen = () => {
          if (closedByEffect) return;
          setTransport("realtime");
          clearInterval(heartbeat);
          heartbeat = setInterval(() => {
            if (ws?.readyState === WebSocket.OPEN) ws.send("ping");
          }, 20000);
        };
        ws.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (payload?.type === "state") {
              setLastContactAt(Date.now());
              apply(payload.state);
            }
          } catch {
            // Polling remains the safety net.
          }
        };
        ws.onerror = () => {
          if (!closedByEffect) setTransport("polling");
        };
        ws.onclose = () => {
          clearInterval(heartbeat);
          if (closedByEffect) return;
          setTransport("polling");
          retry = setTimeout(connect, 2000);
        };
      } catch {
        if (closedByEffect) return;
        setTransport("polling");
        retry = setTimeout(connect, 3000);
      }
    };

    connect();
    const fallback = setInterval(() => poll().catch(() => {}), 5000);

    return () => {
      aliveRef.current = false;
      closedByEffect = true;
      clearInterval(fallback);
      clearInterval(heartbeat);
      clearTimeout(retry);
      for (const timer of timersRef.current) clearTimeout(timer);
      timersRef.current.clear();
      try { ws?.close(); } catch {}
    };
  }, [apply, poll, tableId]);

  useEffect(() => {
    if (state?.seconds_left == null) return undefined;
    const id = setInterval(() => setClockTick((tick) => tick + 1), 250);
    return () => clearInterval(id);
  }, [state?.seconds_left, receivedAt]);

  const secondsLeft = (() => {
    if (state?.seconds_left == null) return null;
    const elapsed = Math.floor((Date.now() - receivedAt) / 1000);
    return Math.max(0, Math.ceil(Number(state.seconds_left) - elapsed));
  })();

  return { state, setState: apply, poll, transport, secondsLeft, lastUpdateAt: lastContactAt, loadError };
}
